import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarRange, Loader2, LockKeyhole, MapPin, Trash2 } from 'lucide-react'
import type { WaybookTrip } from '../../types'

interface TripEditorCardProps {
  trip: WaybookTrip
  onSave: (id: string, payload: Partial<WaybookTrip>) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

export function TripEditorCard({ trip, onSave, onDelete }: TripEditorCardProps) {
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)
  const [title, setTitle] = useState(trip.title ?? '')
  const [description, setDescription] = useState(trip.description ?? '')
  const [status, setStatus] = useState(trip.status)
  const [privacy, setPrivacy] = useState(trip.privacy)
  const [type, setType] = useState(trip.type)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const hasChanges =
    title !== (trip.title ?? '') ||
    description !== (trip.description ?? '') ||
    status !== trip.status ||
    privacy !== trip.privacy ||
    type !== trip.type

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      await onSave(trip.id, {
        title,
        description,
        status,
        privacy,
        type,
      })
      setMessage('Trip updated successfully')
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update trip'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this trip? This cannot be undone.')) {
      return
    }
    setDeleting(true)
    setError(null)
    setMessage(null)
    try {
      await onDelete(trip.id)
      setMessage('Trip deleted.')
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to delete trip'
      setError(message)
    } finally {
      setDeleting(false)
    }
  }

  const resetChanges = () => {
    setTitle(trip.title ?? '')
    setDescription(trip.description ?? '')
    setStatus(trip.status)
    setPrivacy(trip.privacy)
    setType(trip.type)
    setMessage(null)
    setError(null)
    setIsEditing(false)
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
      {isEditing ? (
        <>
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{trip.title || 'Untitled trip'}</h3>
              <p className="text-xs text-gray-500">
                Trip ID: <span className="font-mono">{trip.id}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 transition"
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>

          <div className="mt-4 grid gap-4">
            <div>
              <label htmlFor={`trip-title-${trip.id}`} className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                id={`trip-title-${trip.id}`}
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                className="input w-full"
                placeholder="Trip title"
              />
            </div>

            <div>
              <label
                htmlFor={`trip-description-${trip.id}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <textarea
                id={`trip-description-${trip.id}`}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className="input w-full min-h-[100px]"
                placeholder="Short summary..."
              />
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`trip-status-${trip.id}`}>
                  Status
                </label>
                <select
                  id={`trip-status-${trip.id}`}
                  className="input w-full"
                  value={status}
                  onChange={(event) => setStatus(event.target.value as WaybookTrip['status'])}
                >
                  <option value="planning">Planning</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`trip-privacy-${trip.id}`}>
                  Privacy
                </label>
                <select
                  id={`trip-privacy-${trip.id}`}
                  className="input w-full"
                  value={privacy}
                  onChange={(event) => setPrivacy(event.target.value as WaybookTrip['privacy'])}
                >
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`trip-type-${trip.id}`}>
                  Type
                </label>
                <select
                  id={`trip-type-${trip.id}`}
                  className="input w-full"
                  value={type}
                  onChange={(event) => setType(event.target.value as WaybookTrip['type'])}
                >
                  <option value="personal">Personal</option>
                  <option value="shared">Shared</option>
                  <option value="live">Live</option>
                </select>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{trip.title || 'Untitled trip'}</h3>
              <p className="text-xs text-gray-500">ID: <span className="font-mono">{trip.id}</span></p>
            </div>
            <span className="text-xs uppercase tracking-wide text-gray-500">{trip.status}</span>
          </div>

          {trip.description && (
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{trip.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              <LockKeyhole className="h-3.5 w-3.5" />
              {trip.privacy}
            </span>
            <span>{trip.type}</span>
            {trip.start_date && (
              <span className="inline-flex items-center gap-1">
                <CalendarRange className="h-3.5 w-3.5" />
                {trip.start_date} → {trip.end_date || 'open'}
              </span>
            )}
          </div>

          {trip.locations && trip.locations.length > 0 && (
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" />
              <span>{trip.locations.map((location) => location.title).filter(Boolean).join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {(error || message) && (
        <div
          className={`mt-4 rounded-lg px-3 py-2 text-sm ${
            error ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
          }`}
        >
          {error || message}
        </div>
      )}

      {isEditing ? (
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 text-white px-4 py-2 text-sm font-medium hover:bg-gray-800 transition disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={resetChanges}
            className="text-sm text-gray-600 hover:text-gray-800 transition"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            Edit trip
          </button>
          <button
            type="button"
            onClick={() => navigate(`/user/trips/${trip.id}`)}
            className="text-sm text-gray-600 hover:text-gray-800 transition"
          >
            Open details
          </button>
        </div>
      )}
    </div>
  )
}
